import { useRef, useState } from 'react';
import { attachmentsApi } from '../features/attachments/attachmentsApi';
import type { Attachment, AttachmentResource } from '../features/attachments/types';
import { ATTACHMENTS_MAX_BYTES } from '../features/attachments/types';
import { showToast } from './ui/toast.utils';
import { extractApiMessage } from '../lib/apiError';

type Props = {
  resourceType: AttachmentResource;
  resourceId: string;
  onUploaded?: (a: Attachment) => void;
  accept?: string;
  maxBytes?: number;
  disabled?: boolean;
  label?: string;
  showFeedback?: boolean;
};

export function FileUploadButton({
  resourceType,
  resourceId,
  onUploaded,
  accept,
  maxBytes = ATTACHMENTS_MAX_BYTES,
  disabled = false,
  label = 'Subir archivo',
  showFeedback = true,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File) => {
    setError(null);
    if (file.size > maxBytes) {
      const msg = `El archivo supera el máximo de ${formatBytes(maxBytes)}`;
      setError(msg);
      showToast(msg, 'warning');
      return;
    }
    if (accept) {
      const allowed = accept.split(',').map((s) => s.trim()).filter(Boolean);
      const ok = allowed.some((pattern) => matchAccept(file, pattern));
      if (!ok) {
        const msg = 'Tipo de archivo no permitido';
        setError(msg);
        showToast(msg, 'warning');
        return;
      }
    }

    setBusy(true);
    setProgress(0);
    const toastConfig = showFeedback ? { skipErrorToast: true } : undefined;
    try {
      const init = await attachmentsApi.init({
        resourceType,
        resourceId,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      }, toastConfig);

      await attachmentsApi.upload(init.attachmentId, file, setProgress, toastConfig);
      const attachment = await attachmentsApi.complete(init.attachmentId, toastConfig);
      if (showFeedback) showToast('Archivo subido correctamente.', 'success');
      onUploaded?.(attachment);
    } catch (e: unknown) {
      const msg = extractApiMessage(e, 'Error al subir archivo');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setBusy(false);
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        disabled={disabled || busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <button
        type="button"
        className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
        disabled={disabled || busy || !resourceId}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Subiendo…' : label}
      </button>
      {progress !== null && (
        <div className="h-1.5 w-full max-w-xs rounded bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function matchAccept(file: File, pattern: string): boolean {
  if (pattern.startsWith('.')) {
    return file.name.toLowerCase().endsWith(pattern.toLowerCase());
  }
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -1);
    return (file.type || '').startsWith(prefix);
  }
  return file.type === pattern;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
